import { datadogRum } from "@datadog/browser-rum";
import { LandingLayoutWithLandingDescription } from "PFApp/components/landing_layout";
import {
  isSafeReturnTo,
  stashOauthReturnTo,
  submitOauthSessionBridge
} from "PFApp/helpers/oauth_session_bridge";
import { useSession } from "PFApp/use_session";
import { Alert } from "PFComponents/alert";
import { Button } from "PFComponents/button";
import ButtonLink from "PFComponents/button/button_link";
import { LoadingDots } from "PFComponents/loading_dots";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useStringParamValue } from "PFCore/hooks";
import { useCurrentAccount } from "PFCore/hooks/queries/account/use_current_account";
import { ApiRoute, UiRoute } from "PFCore/utilities/routes";
import LinkedinLogoIllustration from "PFTheme/graphics/illustrations/illustration-linkedin-logo.svg";
import { FeatureFlag } from "PFTypes";
import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useMount } from "react-use";

import css from "./sigin_in_page.module.scss";

type SignInPageProps = {
  adminPath?: boolean;
};

export const SignInPage = ({ adminPath }: SignInPageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [redirectingToSamlUrl, setRedirectingToSamlUrl] = useState(false);

  const { data: currentAccount } = useCurrentAccount();
  const isEnabled = useIsFeatureEnabled();
  const { signIn } = useSession();
  const [searchParams] = useSearchParams();
  const errorParam = searchParams.get("error");
  const returnTo = useStringParamValue("return_to");
  const urlErrorMessageParam = errorParam ? decodeURIComponent(errorParam)?.split("=") : null;

  useMount(() => {
    if (!urlErrorMessageParam) {
      return;
    }

    const errorValue = urlErrorMessageParam?.[1];
    const errorKey = urlErrorMessageParam?.[0];
    const urlErrorMessage =
      errorValue ||
      (errorKey
        ? t(`signUpSignIn.api.${errorKey}` as unknown as TemplateStringsArray, { defaultValue: errorKey })
        : "");
    if (urlErrorMessage && urlErrorMessage.length > 0) {
      const errorMessage = urlErrorMessage.split("+").join(" ");
      setError(errorMessage);
      setTimeout(() => {
        window.history.replaceState(null, "", window.location.pathname);
      }, 1000);
    }
  });

  useEffect(() => {
    if (redirectingToSamlUrl) {
      stashOauthReturnTo(returnTo);
      navigate("/authsaml");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectingToSamlUrl]);

  if (redirectingToSamlUrl) {
    return null;
  }

  if (
    !adminPath &&
    isEnabled(FeatureFlag.AutoSaml) &&
    !window.storage.getItem("samlAttempt") &&
    !sessionStorage?.getItem("signedOut")
  ) {
    window.storage.setItem("samlAttempt", true, { expiresIn: 60 });
    setRedirectingToSamlUrl(true);
  }

  const signInWithPassword = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); // form's onSubmit
    setIsLoading(true);
    setError(null);

    signIn(email.trim(), password, { onlySuccess: true })
      .then((resp) => {
        if (returnTo && isSafeReturnTo(returnTo)) {
          submitOauthSessionBridge(resp.accessToken, returnTo);
        }
      })
      .catch((resp) => {
        setIsLoading(false);
        const errors = resp.response?.data?.errors;
        if (errors) {
          if (errors.length > 0) {
            setError(errors[0].detail);
          }
        } else if (navigator.onLine) {
          setError(t("signUpSignIn.errors.connectionError"));
          datadogRum?.addError(new Error("Sign in connection error"), {
            responseStatus: resp && resp.status
          });
        } else {
          setError(t("signUpSignIn.errors.networkError"));
        }
      });
  };

  const isPasswordSignInEnabled = isEnabled(FeatureFlag.PasswordSignin) || adminPath;
  const isLinkedinSignInEnabled = isEnabled(FeatureFlag.LinkedinSignin);
  const isSamlSignInEnabled = isEnabled(FeatureFlag.SamlSignin);
  const isRequestAccessEnabled = isEnabled(FeatureFlag.RequestAccess);

  return (
    <LandingLayoutWithLandingDescription>
      <strong data-qa-id="SignInPage.header" style={{ marginBottom: 24 }}>
        {isSamlSignInEnabled || isLinkedinSignInEnabled ? t("signUpSignIn.loginWith") : t("login")}
      </strong>

      {isLinkedinSignInEnabled && (
        <Button
          qaId="SignInPage.Button.linkedin"
          style={{ width: "100%" }}
          onClick={() => {
            stashOauthReturnTo(returnTo);
            // @ts-expect-error
            window.location = ApiRoute(
              `/api/oauth/authorize?account=${currentAccount.full_domain}&client_id=${
                window.PF.config.client_id
              }&grant_type=linkedin&redirect_uri=${UiRoute("/")}`
            );
          }}
        >
          <LinkedinLogoIllustration height={20} width={45} style={{ transform: "scale(1.5)" }} />
        </Button>
      )}

      {isSamlSignInEnabled && (
        <ButtonLink
          kind="primary"
          qaId="SignInPage.Button.saml"
          style={{ margin: "8px 0 32px 0", width: "100%" }}
          href={"/authsaml"}
          onClick={() => stashOauthReturnTo(returnTo)}
        >
          {t("signUpSignIn.oneClickLogin")}
        </ButtonLink>
      )}

      {(isSamlSignInEnabled || isLinkedinSignInEnabled) && isPasswordSignInEnabled && (
        <strong>{t("signUpSignIn.orUseYourEmail")}</strong>
      )}

      {error && (
        <Alert kind="error" qaId="SignInPage.error" style={{ marginBottom: 24 }}>
          {error}
        </Alert>
      )}

      {isPasswordSignInEnabled && (
        <form onSubmit={signInWithPassword} aria-label={t("login")} data-qa-id="SignInPage.form">
          <InputFieldSet
            qaId="SignInPage.email"
            label={t("emailAddress")}
            className={css.input}
            style={{ marginBottom: 8 }}
            onChange={setEmail}
            autofocus
          />

          <InputFieldSet
            qaId="SignInPage.password"
            label={t("password")}
            inputType="password"
            className={css.input}
            style={{ marginBottom: 24 }}
            onChange={setPassword}
          />

          <Button qaId="SignInPage.Button.emailAndPasswordSignIn" kind="primary" className={css.button}>
            {isLoading ? <LoadingDots /> : t("login")}
          </Button>

          {!adminPath && (
            <div data-qa-id="SignInPage.reset" style={{ marginBottom: 24 }}>
              <Link to="/users/reset_password">{t("signUpSignIn.resetPassword")}</Link>
            </div>
          )}
        </form>
      )}

      {isPasswordSignInEnabled && !adminPath && (
        <>
          <div style={{ marginBottom: 8 }}>
            <span>{t("signUpSignIn.dontHaveAnAccount")}</span>
          </div>
          <div>
            <Link
              data-qa-id={isRequestAccessEnabled ? "SignInPage.requestAccess" : "SignInPage.signUp"}
              to={isRequestAccessEnabled ? "/request_access" : "/users/signup"}
            >
              {t("signUpSignIn.signUpForOne")}
            </Link>
          </div>
        </>
      )}
    </LandingLayoutWithLandingDescription>
  );
};
